const EXPAND_THRESHOLD_PX = 1;

/**
 * @param {Element} element
 * @returns {string}
 */
function getNormalizedText(element) {
  const clone = /** @type {Element} */ (element.cloneNode(true));
  clone.querySelectorAll('.plp-intro-read-more').forEach(el => el.remove());
  return (clone.textContent || '').replace(/\u00a0/g, ' ').trim();
}

/**
 * @param {Element} description
 * @param {HTMLButtonElement} button
 */
function resetBlock(description, button) {
  description.classList.remove('is-expanded');
  button.hidden = true;
  button.style.display = 'none';
  button.setAttribute('aria-expanded', 'false');
  if (!button.textContent || button.textContent === 'Read less') {
    button.textContent = 'Read more';
  }
}

/**
 * @param {HTMLElement} descEl
 * @param {number} width
 * @returns {HTMLElement}
 */
function makeClone(descEl, width) {
  const clone = /** @type {HTMLElement} */ (descEl.cloneNode(true));
  clone.style.cssText = `position:absolute;visibility:hidden;-webkit-line-clamp:none;display:block;overflow:visible;max-height:none;width:${width}px`;
  return clone;
}

/**
 * @param {Element} description
 * @param {HTMLButtonElement} button
 * @param {number} fullHeight
 * @param {number} clientHeight
 * @param {boolean} wasExpanded
 */
function applyMeasurement(description, button, fullHeight, clientHeight, wasExpanded) {
  const canExpand = fullHeight - clientHeight > EXPAND_THRESHOLD_PX;
  if (wasExpanded && canExpand) description.classList.add('is-expanded');
  button.hidden = !canExpand;
  button.style.display = canExpand ? '' : 'none';
  if (!canExpand) {
    button.setAttribute('aria-expanded', 'false');
    button.textContent = 'Read more';
  }
}

/**
 * Single-block update used by bootstrapBuilderReadMore.
 * @param {Element} block
 */
function updateBlock(block) {
  const description = block.querySelector('.plp-intro-description');
  const button = /** @type {HTMLButtonElement | null} */ (block.querySelector('.plp-intro-read-more'));
  if (!description || !button) return;
  if (!getNormalizedText(description)) { resetBlock(description, button); return; }

  const descEl = /** @type {HTMLElement} */ (description);
  const wasExpanded = descEl.classList.contains('is-expanded');
  if (wasExpanded) descEl.classList.remove('is-expanded');

  // Read clientHeight before clone insert so both reads happen before any DOM write
  const clientHeight = descEl.clientHeight;
  const clone = makeClone(descEl, descEl.offsetWidth);
  (descEl.parentElement || document.body).insertBefore(clone, descEl.nextSibling);
  const fullHeight = clone.scrollHeight;
  clone.remove();

  applyMeasurement(description, button, fullHeight, clientHeight, wasExpanded);
}

function updateAllBlocks() {
  const blocks = [...document.querySelectorAll('.plp-intro-block')];
  if (!blocks.length) return;

  // Phase 1: collect entries and batch-remove is-expanded (all writes before reads)
  /** @type {Array<{ descEl: HTMLElement, button: HTMLButtonElement, wasExpanded: boolean }>} */
  const entries = [];
  for (const block of blocks) {
    const description = block.querySelector('.plp-intro-description');
    const button = /** @type {HTMLButtonElement | null} */ (block.querySelector('.plp-intro-read-more'));
    if (!description || !button) continue;
    if (!getNormalizedText(description)) { resetBlock(description, button); continue; }
    const descEl = /** @type {HTMLElement} */ (description);
    const wasExpanded = descEl.classList.contains('is-expanded');
    if (wasExpanded) descEl.classList.remove('is-expanded');
    entries.push({ descEl, button, wasExpanded });
  }
  if (!entries.length) return;

  // Phase 2: batch-read all widths + clientHeights (one layout flush)
  const widths = entries.map(e => e.descEl.offsetWidth);
  const clientHeights = entries.map(e => e.descEl.clientHeight);

  // Phase 3: batch-insert all clones (writes)
  const clones = entries.map((e, i) => {
    const c = makeClone(e.descEl, widths[i]);
    (e.descEl.parentElement || document.body).insertBefore(c, e.descEl.nextSibling);
    return c;
  });

  // Phase 4: batch-read all full heights (one layout flush)
  const fullHeights = clones.map(c => c.scrollHeight);

  // Phase 5: remove clones + apply results (writes)
  clones.forEach(c => c.remove());
  entries.forEach((e, i) => applyMeasurement(e.descEl, e.button, fullHeights[i], clientHeights[i], e.wasExpanded));
}

/** @type {number | null} */
let rafId = null;

function scheduleUpdate() {
  if (rafId !== null) return;

  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    updateAllBlocks();
  });
}

function initPlpIntroReadMore() {
  scheduleUpdate();
  window.setTimeout(scheduleUpdate, 100);

  window.addEventListener('resize', scheduleUpdate);
  window.addEventListener('load', scheduleUpdate);

  const observer = new MutationObserver(scheduleUpdate);
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPlpIntroReadMore, { once: true });
} else {
  initPlpIntroReadMore();
}

/**
 * Bootstrap Builder.io blocks that have the `builder-read-more` class.
 * Adds the plp-intro DOM structure so the existing logic can take over.
 */
function bootstrapBuilderReadMore() {
  document.querySelectorAll('.builder-read-more:not([data-read-more-init])').forEach((block) => {
    block.setAttribute('data-read-more-init', '1');
    block.classList.add('plp-intro-block', 'plp-intro-block--builder');

    // Use existing .plp-intro-description if present, otherwise wrap first child element
    if (!block.querySelector('.plp-intro-description')) {
      const firstChild = block.children[0];
      if (firstChild) firstChild.classList.add('plp-intro-description');
    }

    // If .plp-intro-description is a non-<p> wrapper (e.g. span.builder-text > p),
    // move the class to the inner <p> so the JS overflow check and CSS clamp target the same element
    const desc = block.querySelector('.plp-intro-description');
    if (desc && desc.tagName !== 'P') {
      const innerP = desc.querySelector('p');
      if (innerP) {
        desc.classList.remove('plp-intro-description');
        innerP.classList.add('plp-intro-description');
      }
    }

    // Inject button with More/Less toggle if not already present
    if (!block.querySelector('.plp-intro-read-more')) {
      const btn = document.createElement('button');
      btn.className = 'plp-intro-read-more';
      btn.hidden = true;
      btn.style.display = 'none';
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = 'Read more';
      btn.addEventListener('click', () => {
        const description = block.querySelector('.plp-intro-description');
        if (!description) return;
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        description.classList.toggle('is-expanded', !isExpanded);
        btn.setAttribute('aria-expanded', String(!isExpanded));
        btn.textContent = isExpanded ? 'Read more' : 'Read less';
      });
      const descTarget = block.querySelector('.plp-intro-description');
      (descTarget || block).appendChild(btn);
    }

    // Defer overflow check to after the browser recalculates layout with the new classes
    requestAnimationFrame(() => {
      updateBlock(block);
      // Second pass handles slow-rendering Builder components
      setTimeout(() => updateBlock(block), 150);
    });
  });
}

// Run on load and watch for Builder lazy-rendering new blocks
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapBuilderReadMore, { once: true });
} else {
  bootstrapBuilderReadMore();
}

const builderObserver = new MutationObserver(bootstrapBuilderReadMore);
builderObserver.observe(document.body, { childList: true, subtree: true });